import * as domain from "../domain/index"
import { BaseUser } from "./user.base"
import { versionInfo as version } from "../version"

// built in buckets, present in all organizations
export const DefaultBucketName = {
    end_user_read_write: "end_user_read_write",
    end_user_read_only: "end_user_read_only",
    end_user_no_access: "end_user_no_access",
    "person_pool-end_user_read_write": "person_pool-end_user_read_write",
    "person_pool-end_user_read_only": "person_pool-end_user_read_only",
    "person_pool-end_user_no_access": "person_pool-end_user_no_access",
} as const

export type BucketName = keyof typeof DefaultBucketName | string

export type BucketOptions = {
    user: BaseUser
    bucketName: BucketName
}

/**
 *  An object that allows you to access and modify the attributes stored within the named bucket.
 *  The easiest way to create a bucket is by using the {@link Types.BaseUser.getBucket} method and passing in the bucket name.
 */
export class Bucket {
    private user: BaseUser
    private bucketName: BucketName

    constructor({ user, bucketName }: BucketOptions) {
        this.user = user
        this.bucketName = bucketName
    }

    /**
     * Retrieve attributes for a person from the bucket
     *
     * @param attributeNames You can optionally filter which attributes to retrieve by supplying a list of attribute names. If you don't the method returns all attributes.
     *
     * @returns A map from `string` to basic types containing user data previously associated with {@link set}.
     * @throws `Error` if the operation fails.
     */
    public async get<AttributesType extends domain.JsonObject>(attributeNames?: string[]): Promise<AttributesType> {
        const response = await this.user
            .getAttributesClient()
            .getAttributesBucketName(
                { bucketName: this.bucketName, attributes: attributeNames, slashIDSdkVersion: version.raw },
                { cache: "no-cache" }
            )

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return response.result as AttributesType
    }

    /**
     * Set attributes for a person in the bucket
     *
     * @param attributes A serializable object to associate with the user credential.
     * @throws `Error` if the operation fails.
     */
    public async set<AttributesType extends domain.JsonObject>(attributes: AttributesType): Promise<void> {
        const response = await this.user.getAttributesClient().putAttributesBucketName(
            {
                bucketName: this.bucketName,
                body: attributes,
                slashIDSdkVersion: version.raw,
            },
            {
                cache: "no-cache",
            }
        )

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return
    }

    /**
     * Delete person attributes from the bucket
     *
     * @param attributeNames You must specify which data attributes to delete by supplying a list of filter strings.
     * @throws `Error` if the operation fails.
     */
    public async delete(attributeNames: string[]): Promise<any> {
        const response = await this.user
            .getAttributesClient()
            .deleteAttributesBucketName(
                { bucketName: this.bucketName, slashIDSdkVersion: version.raw, attributes: attributeNames },
                { cache: "no-cache" }
            )

        const errors = response.errors
        if (errors) {
            throw errors
        }

        return
    }
}
